import { useEffect, useRef, useState } from 'react';
import { ArrowRight, AudioLines, Building2, CircleMinus, CirclePlus, House, Languages, Link, LogIn, MessageCircle, Pause, Play, Scissors, Settings2, Smartphone, Store, Utensils } from 'lucide-react';

type HomeView = 'hello' | 'why-telx' | 'how-it-works';

const audiences = [
  { name: 'Restaurants', icon: Utensils },
  { name: 'Realtors', icon: House },
  { name: 'Salons', icon: Scissors },
  { name: 'Hotels', icon: Building2 },
  { name: 'Small businesses', icon: Store },
];

function AudienceRotator({ active }: { active: boolean }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(preference.matches);
    sync();
    preference.addEventListener('change', sync);
    return () => preference.removeEventListener('change', sync);
  }, []);
  useEffect(() => {
    if (!active || paused || reducedMotion) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setIndex(current => (current + 1) % audiences.length);
    }, 3600);
    return () => window.clearInterval(timer);
  }, [active, paused, reducedMotion]);
  const { name, icon: Icon } = audiences[index];
  return <div className="home-audience">
    <span className="sr-only">Restaurants, realtors, salons, hotels, and small businesses.</span>
    <span className="home-audience-window" aria-hidden="true"><span className="home-audience-word" key={name}><Icon strokeWidth={1.7} />{name}.</span></span>
    <button className="home-motion-toggle" type="button" onClick={() => setPaused(value => !value)}
      aria-label={paused ? 'Resume rotating business types' : 'Pause rotating business types'} title={paused ? 'Resume animation' : 'Pause animation'}>
      {paused ? <Play size={15} aria-hidden="true" /> : <Pause size={15} aria-hidden="true" />}
    </button>
  </div>;
}

function WhyTelx() {
  const benefits = [
    { icon: Languages, title: 'A different language shouldn’t mean a missed conversation.', copy: 'Talk with customers, clients, and the people who matter to you. Live translation helps you understand each other as you speak.' },
    { icon: AudioLines, title: 'Speak naturally. Still sound like you.', copy: 'Use a standard voice, or set up My voice so your translated speech can sound like you on supported calls. The choice is yours.' },
    { icon: Settings2, title: 'Your conversation. Your choice of language.', copy: 'Choose what language the other person hears and what you want to hear. On a two-person phone call, you can change those choices during the conversation.' },
  ];
  return <>
    <div className="home-benefits">{benefits.map(({ icon: Icon, title, copy }) => <details key={title}>
      <summary><Icon className="home-row-icon" aria-hidden="true" strokeWidth={1.6} /><span>{title}</span><CirclePlus className="home-expand" aria-hidden="true" strokeWidth={1.5} /><CircleMinus className="home-collapse" aria-hidden="true" strokeWidth={1.5} /></summary>
      <p>{copy}</p>
    </details>)}</div>
  </>;
}

function HowTelxWorks() {
  const steps = [
    { icon: LogIn, text: 'Open Telx and sign in.' },
    { icon: Settings2, text: 'Choose your voice and translation languages.' },
    { icon: Link, text: 'Create a call and share its link. Or call a US number from Telx on iPhone.' },
    { icon: MessageCircle, text: 'Speak naturally. Hear live translation in your chosen language.' },
  ];
  return <>
    <ol className="home-steps">{steps.map(({ icon: Icon, text }) => <li key={text}><Icon className="home-row-icon" aria-hidden="true" strokeWidth={1.6} /><p>{text}</p></li>)}</ol>
  </>;
}

export function PublicHome() {
  const [view, setView] = useState<HomeView>('hello');
  const main = useRef<HTMLElement>(null);
  const toggle = (next: HomeView) => setView(current => current === next ? 'hello' : next);
  return <div className="simple-home">
    <a className="skip-link" href="#main" onClick={event => {
      event.preventDefault(); main.current?.focus(); main.current?.scrollIntoView({ block: 'start' });
    }}>Skip to content</a>
    <header className="simple-home-header"><a href="mailto:support@riftco.io">Say hello <span>support@riftco.io</span></a></header>
    <main id="main" ref={main} tabIndex={-1} className="simple-home-main home-hello">
      <h1><Smartphone className="home-heading-icon" aria-hidden="true" strokeWidth={1.6} />Hello, it’s <span className="home-wordmark">Telx</span>.</h1>
      <div className="home-content">
        <section className={`home-panel ${view === 'hello' ? 'is-active' : ''}`} aria-label="Built for your business" aria-hidden={view !== 'hello'} inert={view !== 'hello'}>
          <p className="home-introduction">Calls that speak your language.<br />Built for</p>
          <AudienceRotator active={view === 'hello'} />
        </section>
        <section id="why-telx" className={`home-panel ${view === 'why-telx' ? 'is-active' : ''}`} aria-label="Why Telx" aria-hidden={view !== 'why-telx'} inert={view !== 'why-telx'}>
          <WhyTelx key={view === 'why-telx' ? 'open' : 'closed'} />
        </section>
        <section id="how-it-works" className={`home-panel ${view === 'how-it-works' ? 'is-active' : ''}`} aria-label="How it works" aria-hidden={view !== 'how-it-works'} inert={view !== 'how-it-works'}>
          <HowTelxWorks />
        </section>
      </div>
      <div className="simple-home-actions">
        <a className="simple-home-start" href="/app">Open Telx <ArrowRight size={20} aria-hidden="true" /></a>
        <div className="home-view-navigation" role="group" aria-label="About Telx">
          <button type="button" onClick={() => toggle('why-telx')} aria-expanded={view === 'why-telx'} aria-controls="why-telx">Why Telx</button>
          <button type="button" onClick={() => toggle('how-it-works')} aria-expanded={view === 'how-it-works'} aria-controls="how-it-works">How it works</button>
        </div>
      </div>
    </main>
    <footer className="simple-home-footer"><span>Telx by Riftco</span><nav aria-label="Information"><a href="/support">Support</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a></nav></footer>
  </div>;
}
